import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Images,
  LayoutGrid,
  List,
  Menu,
  MessageSquare,
  MessagesSquare,
  Paperclip,
  Plus,
  Search,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { LIMITS, Permission, type Attachment, type Channel, type ForumLayout, type ForumPost, type ForumTag } from '@spawnwire/shared';
import { Avatar, Button, clsx, DropdownMenu, EmptyState, Field, IconButton, Input, Modal, Select, Spinner, TextArea, Tooltip, type MenuEntry } from '../../components/ui';
import { errorMessage, http, uploadFile } from '../../lib/api';
import { attempt } from '../../lib/data';
import { useIsMobile } from '../../lib/hooks';
import { plainText } from '../../lib/markdown';
import { setCurrentChannel } from '../../lib/nav';
import { can, useChannelPermissions } from '../../lib/perms';
import { formatBytes, formatTimestamp } from '../../lib/time';
import { actions, displayNameIn, useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { Composer } from '../chat/Composer';
import { MessageList } from '../chat/MessageList';
import { PanelHeader } from '../chat/PanelHeader';
import { TypingIndicator } from '../chat/TypingIndicator';

/** Time encoded in a (time-ordered) id, for "last activity". */
const idTime = (id: string) => new Date(parseInt(id.replace(/-/g, '').slice(0, 12), 16)).toISOString();

const isVisual = (a: Attachment) => a.contentType.startsWith('image/') || a.contentType.startsWith('video/');

export function TagChip({ tag, active, onClick }: { tag: ForumTag; active?: boolean; onClick?: () => void }) {
  const body = (
    <>
      {tag.emoji && <span aria-hidden>{tag.emoji}</span>}
      {tag.name}
    </>
  );
  const cls = clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', active ? 'bg-accent text-accent-fg' : 'bg-active text-fg');
  return onClick ? (
    <button type="button" onClick={onClick} aria-pressed={active} className={clsx(cls, !active && 'hover:bg-line-strong')}>
      {body}
    </button>
  ) : (
    <span className={cls}>{body}</span>
  );
}

function tagsOf(forum: Channel | undefined, ids: string[] | undefined): ForumTag[] {
  return (ids ?? []).map((id) => forum?.availableTags?.find((t) => t.id === id)).filter((t): t is ForumTag => !!t);
}

function Cover({ attachment, className }: { attachment: Attachment; className?: string }) {
  return attachment.contentType.startsWith('video/') ? (
    <video src={attachment.url} muted playsInline preload="metadata" className={clsx('bg-black object-cover', className)} />
  ) : (
    <img src={`${attachment.url}?preview=1`} alt="" loading="lazy" className={clsx('object-cover', className)} />
  );
}

function PostCard({ post, forum, layout, active }: { post: ForumPost; forum: Channel; layout: ForumLayout; active: boolean }) {
  const thread = useStore((s) => s.channels[post.channel.id]) ?? post.channel;
  const name = useStore((s) => (post.author ? displayNameIn(s, post.author.id, forum.spaceId) : 'Deleted User'));
  const snippet = post.starter ? plainText(post.starter.content, useStore.getState()) : '';
  const cover = post.starter?.attachments.find(isVisual);
  const replies = Math.max(0, (thread.messageCount ?? 1) - 1);
  const lastActive = thread.lastMessageId ? idTime(thread.lastMessageId) : thread.createdAt;
  const tags = tagsOf(forum, thread.appliedTags);
  const open = () => ui.openThread(thread.id);

  if (layout === 'gallery') {
    return (
      <button
        type="button"
        onClick={open}
        className={clsx('flex flex-col overflow-hidden rounded-xl border bg-sidebar text-left transition-colors hover:border-line-strong', active ? 'border-accent' : 'border-line')}
      >
        {cover ? (
          <Cover attachment={cover} className="aspect-[4/3] w-full" />
        ) : (
          <div className="flex aspect-[4/3] w-full items-start bg-main p-3 text-sm text-muted">
            <span className="line-clamp-6">{snippet || 'No preview'}</span>
          </div>
        )}
        <div className="space-y-1.5 p-3">
          <p className="line-clamp-2 font-semibold text-fg">{thread.name}</p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => (
                <TagChip key={t.id} tag={t} />
              ))}
            </div>
          )}
          <p className="flex items-center gap-1.5 text-xs text-muted">
            <Avatar user={post.author} size={16} /> <span className="truncate">{name}</span>
            <span className="ml-auto flex items-center gap-1">
              <MessageSquare className="size-3.5" aria-hidden /> {replies}
            </span>
          </p>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      className={clsx('flex w-full gap-3 rounded-xl border bg-sidebar p-4 text-left transition-colors hover:border-line-strong', active ? 'border-accent' : 'border-line')}
    >
      <Avatar user={post.author} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-fg">{thread.name}</span>
          {thread.archived && <span className="rounded bg-active px-1.5 text-[11px] text-muted">Archived</span>}
        </div>
        {tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {tags.map((t) => (
              <TagChip key={t.id} tag={t} />
            ))}
          </div>
        )}
        {snippet && <p className="mt-1 line-clamp-2 text-sm text-muted">{snippet}</p>}
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-faint">
          <span className="font-medium text-muted">{name}</span>
          <span className="flex items-center gap-1">
            <MessageSquare className="size-3.5" aria-hidden /> {replies} {replies === 1 ? 'reply' : 'replies'}
          </span>
          <span>Active {formatTimestamp(lastActive)}</span>
        </p>
      </div>
      {cover && <Cover attachment={cover} className="size-20 shrink-0 rounded-lg" />}
    </button>
  );
}

interface Upload {
  key: string;
  file: File;
  progress: number;
  attachment: Attachment | null;
  error: string | null;
}

function NewPostDialog({ forum, onClose, onCreated }: { forum: Channel; onClose: () => void; onCreated: (post: ForumPost) => void }) {
  const perms = useChannelPermissions(forum.id);
  const canManage = can(perms, Permission.MANAGE_MESSAGES);
  const canAttach = can(perms, Permission.ATTACH_FILES);
  const media = forum.type === 'media';
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const tags = (forum.availableTags ?? []).filter((t) => canManage || !t.moderated);

  function addFiles(files: File[]) {
    for (const file of files.slice(0, LIMITS.attachmentsPerMessage - uploads.length)) {
      const key = `${file.name}-${Math.random().toString(36).slice(2)}`;
      setUploads((u) => [...u, { key, file, progress: 0, attachment: null, error: null }]);
      uploadFile<Attachment>(`/channels/${forum.id}/attachments`, file, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))))
        .promise.then((attachment) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, attachment, progress: 1 } : x))))
        .catch((err) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: errorMessage(err) } : x))));
    }
  }

  const ready = uploads.filter((u) => u.attachment).map((u) => u.attachment!);
  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const hasVisual = ready.some(isVisual);
  const needsTag = forum.requireTag && tags.length > 0 && tagIds.length === 0;
  const missing = !title.trim() ? 'Give your post a title.' : media && !hasVisual ? 'Add at least one image or video.' : !media && !content.trim() && ready.length === 0 ? 'Write something.' : needsTag ? 'Pick a tag.' : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (missing || uploading) return;
    setBusy(true);
    setError(null);
    try {
      const post = await http.post<ForumPost>(`/channels/${forum.id}/posts`, { title: title.trim(), content, tagIds, attachmentIds: ready.map((a) => a.id) });
      onCreated(post);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={media ? 'New media post' : 'New post'}
      description={forum.topic || undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!!missing || uploading} onClick={(e) => void submit(e as unknown as FormEvent)}>
            Post
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title" htmlFor="post-title">
          <Input id="post-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} autoFocus placeholder={media ? 'What are you sharing?' : 'What is your post about?'} />
        </Field>
        <Field label={media ? 'Description (optional)' : 'Message'} htmlFor="post-body">
          <TextArea id="post-body" rows={5} value={content} onChange={(e) => setContent(e.target.value)} maxLength={LIMITS.messageLength} />
        </Field>
        {tags.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
              Tags {forum.requireTag ? '(pick at least one)' : '(optional)'} · up to {LIMITS.tagsPerPost}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <TagChip
                  key={t.id}
                  tag={t}
                  active={tagIds.includes(t.id)}
                  onClick={() => setTagIds((ids) => (ids.includes(t.id) ? ids.filter((x) => x !== t.id) : ids.length < LIMITS.tagsPerPost ? [...ids, t.id] : ids))}
                />
              ))}
            </div>
          </div>
        )}
        {canAttach && (
          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">{media ? 'Images and videos' : 'Attachments'}</p>
              <Button size="sm" variant="secondary" icon={media ? Images : Paperclip} onClick={() => picker.current?.click()} disabled={uploads.length >= LIMITS.attachmentsPerMessage}>
                Add files
              </Button>
              <input
                ref={picker}
                type="file"
                multiple
                hidden
                accept={media ? 'image/*,video/*' : undefined}
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </div>
            {uploads.length > 0 && (
              <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {uploads.map((u) => (
                  <li key={u.key} className="relative overflow-hidden rounded-lg border border-line bg-main">
                    {u.attachment && isVisual(u.attachment) ? (
                      <Cover attachment={u.attachment} className="aspect-video w-full" />
                    ) : (
                      <div className="flex aspect-video flex-col items-center justify-center p-2 text-center text-xs text-muted">
                        <span className="line-clamp-2 break-all">{u.file.name}</span>
                        <span>{u.error ?? (u.attachment ? formatBytes(u.file.size) : `${Math.round(u.progress * 100)}%`)}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${u.file.name}`}
                      onClick={() => {
                        if (u.attachment) void http.del(`/attachments/${u.attachment.id}`).catch(() => {});
                        setUploads((all) => all.filter((x) => x.key !== u.key));
                      }}
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {(error || (missing && title)) && <p className="text-sm text-danger">{error ?? missing}</p>}
      </form>
    </Modal>
  );
}

/** An open post: its messages and replies, with its tags. */
function PostPanel({ forum, threadId }: { forum: Channel; threadId: string }) {
  const thread = useStore((s) => s.channels[threadId]);
  const meId = useStore((s) => s.me?.id);
  const perms = useChannelPermissions(forum.id);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (thread) return;
    http
      .get<Channel>(`/channels/${threadId}`)
      .then((c) => actions.upsertChannel(c))
      .catch(() => setMissing(true));
  }, [threadId, thread]);

  if (!thread) {
    return <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted">{missing ? 'This post is gone.' : <Spinner />}</div>;
  }
  const canManage = can(perms, Permission.MANAGE_MESSAGES);
  const canEdit = thread.ownerId === meId || canManage;
  const applied = thread.appliedTags ?? [];
  const tags = tagsOf(forum, applied);
  const setTags = (ids: string[]) => void attempt(() => http.patch(`/channels/${thread.id}`, { appliedTags: ids }));
  const tagMenu: MenuEntry[] = (forum.availableTags ?? [])
    .filter((t) => canManage || !t.moderated)
    .map((t) => ({
      label: `${applied.includes(t.id) ? '✓ ' : ''}${t.emoji ? `${t.emoji} ` : ''}${t.name}`,
      onSelect: () => setTags(applied.includes(t.id) ? applied.filter((x) => x !== t.id) : [...applied, t.id].slice(0, LIMITS.tagsPerPost)),
    }));
  const close = () => useUi.setState({ threadId: null, rightPanel: null, mobilePane: 'main' });

  return (
    <>
      <PanelHeader
        title={thread.name}
        icon={<IconButton icon={ArrowLeft} label="Back to posts" onClick={close} />}
        actions={
          <>
            {canEdit && tagMenu.length > 0 && <DropdownMenu entries={tagMenu} trigger={<IconButton icon={Tag} label="Edit tags" />} />}
            {canEdit && (
              <IconButton
                icon={thread.archived ? ArchiveRestore : Archive}
                label={thread.archived ? 'Reopen post' : 'Close post'}
                onClick={() => void attempt(() => http.patch(`/channels/${thread.id}`, { archived: !thread.archived }))}
              />
            )}
            {canEdit && (
              <IconButton
                icon={Trash2}
                label="Delete post"
                danger
                onClick={() =>
                  ui.confirm({
                    title: `Delete "${thread.name}"?`,
                    body: 'The post and every reply in it will be deleted.',
                    confirmLabel: 'Delete post',
                    danger: true,
                    onConfirm: async () => {
                      await http.del(`/channels/${thread.id}`);
                      close();
                    },
                  })
                }
              />
            )}
          </>
        }
      />
      {(tags.length > 0 || thread.archived) && (
        <div className="flex flex-wrap items-center gap-1 border-b border-line px-4 py-2">
          {tags.map((t) => (
            <TagChip key={t.id} tag={t} />
          ))}
          {thread.archived && <span className="text-xs text-muted">Closed. Reopen it to reply.</span>}
        </div>
      )}
      <MessageList channelId={thread.id} />
      <div className="relative">
        <Composer channelId={thread.id} placeholder={`Reply to ${thread.name}`} />
        <div className="absolute inset-x-0 bottom-0">
          <TypingIndicator channelId={thread.id} />
        </div>
      </div>
    </>
  );
}

const LAYOUT_KEY = (id: string) => `spawnwire:forum-layout:${id}`;

/** Forum and media channels: a searchable, filterable list (or gallery) of posts. */
export function ForumView({ channelId }: { channelId: string }) {
  const forum = useStore((s) => s.channels[channelId]);
  const perms = useChannelPermissions(channelId);
  const isMobile = useIsMobile();
  const rightPanel = useUi((s) => s.rightPanel);
  const threadId = useUi((s) => s.threadId);
  const mobilePane = useUi((s) => s.mobilePane);
  const openThread = useStore((s) => (threadId ? s.channels[threadId] : undefined));
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [sort, setSort] = useState<'activity' | 'created'>('activity');
  const [status, setStatus] = useState<'open' | 'archived'>('open');
  const [layout, setLayout] = useState<ForumLayout>(() => {
    try {
      return (localStorage.getItem(LAYOUT_KEY(channelId)) as ForumLayout | null) ?? forum?.defaultLayout ?? 'list';
    } catch {
      return forum?.defaultLayout ?? 'list';
    }
  });
  const [posts, setPosts] = useState<ForumPost[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  // Live updates arrive as thread changes in the store; a cheap signature of them triggers a reload.
  const signature = useStore((s) =>
    Object.values(s.channels)
      .filter((c) => c.type === 'thread' && c.parentId === channelId)
      .map((c) => `${c.id}:${c.name}:${c.archived}:${c.lastMessageId}:${(c.appliedTags ?? []).join(',')}`)
      .sort()
      .join('|'),
  );

  useEffect(() => {
    setCurrentChannel(channelId);
    return () => setCurrentChannel(null);
  }, [channelId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(
    async (offset = 0) => {
      try {
        const params = new URLSearchParams({ sort, status, offset: String(offset) });
        if (debounced) params.set('q', debounced);
        if (tag) params.set('tag', tag);
        const res = await http.get<{ posts: ForumPost[]; hasMore: boolean }>(`/channels/${channelId}/posts?${params}`);
        res.posts.forEach((p) => {
          if (!useStore.getState().channels[p.channel.id]) actions.upsertChannel(p.channel);
        });
        setPosts((prev) => (offset && prev ? [...prev, ...res.posts] : res.posts));
        setHasMore(res.hasMore);
        setError(null);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [channelId, sort, status, debounced, tag],
  );

  useEffect(() => {
    const t = setTimeout(() => void load(), signature ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, signature]);

  const tags = useMemo(() => forum?.availableTags ?? [], [forum?.availableTags]);
  if (!forum) return null;

  const media = forum.type === 'media';
  const canPost = can(perms, Permission.CREATE_THREADS);
  const postOpen = rightPanel === 'thread' && !!threadId && (!openThread || openThread.parentId === channelId);
  const showPanel = postOpen && (!isMobile || mobilePane === 'side');
  const Icon = media ? Images : MessagesSquare;
  const setLayoutPersist = (l: ForumLayout) => {
    setLayout(l);
    try {
      localStorage.setItem(LAYOUT_KEY(channelId), l);
    } catch {
      /* per-device preference only */
    }
  };

  return (
    <div className="flex min-h-0 flex-1">
      <section className={clsx('flex min-w-0 flex-1 flex-col', isMobile && showPanel && 'hidden')} aria-label={forum.name}>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3 shadow-sm">
          {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
          <Icon className="size-5 shrink-0 text-faint" aria-hidden />
          <h1 className="truncate font-semibold text-fg">{forum.name}</h1>
          {forum.topic && !isMobile && (
            <>
              <span className="h-5 w-px bg-line" aria-hidden />
              <Tooltip label={forum.topic}>
                <p className="min-w-0 truncate text-sm text-muted">{forum.topic}</p>
              </Tooltip>
            </>
          )}
        </header>
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-48 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search posts" aria-label="Search posts" className="pl-9" />
              </div>
              <div className="w-40">
                <Select
                  value={sort}
                  onChange={setSort}
                  options={[
                    { value: 'activity', label: 'Recent activity' },
                    { value: 'created', label: 'Newest posts' },
                  ]}
                />
              </div>
              <div className="w-28">
                <Select
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'archived', label: 'Closed' },
                  ]}
                />
              </div>
              <div className="flex rounded-md border border-line" role="group" aria-label="Layout">
                <IconButton icon={List} label="List" active={layout === 'list'} onClick={() => setLayoutPersist('list')} />
                <IconButton icon={LayoutGrid} label="Gallery" active={layout === 'gallery'} onClick={() => setLayoutPersist('gallery')} />
              </div>
              {canPost && (
                <Button icon={Plus} onClick={() => setComposing(true)}>
                  New post
                </Button>
              )}
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5" aria-label="Filter by tag">
                {tags.map((t) => (
                  <TagChip key={t.id} tag={t} active={tag === t.id} onClick={() => setTag(tag === t.id ? null : t.id)} />
                ))}
              </div>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            {!posts ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : posts.length === 0 ? (
              <EmptyState
                icon={Icon}
                title={debounced || tag ? 'No posts match' : status === 'archived' ? 'No closed posts' : 'No posts yet'}
                body={debounced || tag ? 'Try a different search or tag.' : canPost ? `Start the first ${media ? 'media post' : 'post'}.` : undefined}
                action={!debounced && !tag && canPost && status === 'open' ? <Button icon={Plus} onClick={() => setComposing(true)}>New post</Button> : undefined}
              />
            ) : (
              <ul className={clsx(layout === 'gallery' ? 'grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4' : 'space-y-2')}>
                {posts.map((p) => (
                  <li key={p.channel.id} className={layout === 'gallery' ? 'flex' : undefined}>
                    <PostCard post={p} forum={forum} layout={layout} active={threadId === p.channel.id && postOpen} />
                  </li>
                ))}
              </ul>
            )}
            {hasMore && posts && (
              <Button variant="secondary" className="w-full" onClick={() => void load(posts.length)}>
                Load more
              </Button>
            )}
          </div>
        </div>
      </section>
      {showPanel && threadId && (
        <aside className={clsx('flex flex-col border-l border-line bg-sidebar', isMobile ? 'w-full' : 'w-[420px] shrink-0')}>
          <PostPanel forum={forum} threadId={threadId} />
        </aside>
      )}
      {composing && <NewPostDialog forum={forum} onClose={() => setComposing(false)} onCreated={(post) => {
        actions.upsertChannel(post.channel);
        setPosts((list) => [post, ...(list ?? []).filter((p) => p.channel.id !== post.channel.id)]);
        ui.openThread(post.channel.id);
      }} />}
    </div>
  );
}
